import * as React from 'react';
import { BookingDetailSection, SectionProps } from '../../SharedSections/BookingDetailSection';
import { FormattedMessage, useIntl } from 'react-intl';
import { isNullOrUndefined } from 'Util/TypeGuards';
import useAsync from 'Hooks/useAsync';
import {
	BookingCheckInStatusHistoryDto,
	getFilteredTrackingHistoryForBooking,
} from 'Services/Api/_HumanWritten/CheckInStatusHistoryService';
import alertToast from 'Util/ToastifyUtils';
import { store } from 'Models/Store';
import Icon from '../../../../Icon/Icon';
import dayjs from 'dayjs';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';

export function TrackingInformationSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const intl = useIntl();

	const response = useAsync(() => getFilteredTrackingHistoryForBooking(booking.id), []);

	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return <></>;
	}

	const freightDetails = booking.bookedSummary.freightDetail;

	if (!response || response.type === 'error') {
		alertToast('Failed to fetch tracking status for booking.', 'error');
		return <></>;
	}

	// We wont show the freight status history if the freight DOES NOT have a status assigned to it,
	// or if the ferry trip has not departed yet
	if (
		isNullOrUndefined(freightDetails) ||
		(isNullOrUndefined(booking.checkInStatusId) &&
			booking.bookedSummary.ferryTrip.departureDateTime.getTime() < new Date().getTime())
	) {
		return null;
	}

	const histories = (response.data as BookingCheckInStatusHistoryDto[]) ?? [];
	const sectionContent = (
		<div className="history-container">
			<div className="history-timeline">
				{histories.map((h, index) => (
					<div key={h.statusId} className={`history-item ${store.isStaff ? 'staff' : ''}`}>
						{index < histories.length - 1 && (
							<div className={`vertical-line ${h.completed ? 'complete' : ''}`} />
						)}
						<div className={`status-circle ${h.completed ? 'completed' : ''}`}>
							{h.completed && <div className="inner-circle" />}
						</div>
						<div className="status-content">
							<p className="status-label">{h.statusLabel}</p>
							{h.timestamp && (
								<div className="status-details">
									{dayjs(h.timestamp).format('DD MMM YY')}, {formatStandardTime(h.timestamp)}
									{h.location && ` - ${h.location}`}
								</div>
							)}
							{store.isStaff && h.completed && (
								<div className="status-staff">
									<Icon name="person" useMaterialIcons />
									<span>{h.staffName}</span>
								</div>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);

	if (isNullOrUndefined(booking.checkInStatusId)) {
		return null;
	}

	return (
		<BookingDetailSection
			sectionName={intl.formatMessage({
				id: 'booking-details__tracking-information-section__title',
				defaultMessage: 'Tracking information',
			})}
			sectionContent={sectionContent}
			sectionClassName="trip-time"
			icon={{
				name: 'calendar_today',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				editType: 'trip-time',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}
